import { useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BusinessIcon from '@mui/icons-material/Business';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HistoryIcon from '@mui/icons-material/History';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';

import { NextThemeProvider, StatCard, sxCard, sxCardAccent, sxTableHead, tokens } from '../../theme-next';
import EntityChangeDialog from './EntityChangeDialog';
import { AgencyAccount, AgencyProfile as AgencyProfileData, useAgencyProfile } from './hooks/useAgencyProfile';

/**
 * Fiche agence — écran de préparation d'un changement d'entité juridique.
 *
 * Une agence Skipcar n'est pas une ligne : elle vit sur six magasins (Firebase
 * Auth, Firestore, Pipedrive, les miroirs MySQL `organizations` / `persons`,
 * les tables métier, Qonto). Cet écran les montre ENSEMBLE, parce que c'est
 * précisément leur dispersion qui oblige aujourd'hui à passer par un dev pour
 * la moindre bascule de fiche.
 */

/**
 * Diagnostic du SIRET. 212 organisations sur 785 ne portent que leur SIREN
 * (9 chiffres) et 32 une valeur inexploitable, alors que ce SIRET part dans les
 * factures Qonto et dans l'archive légale CFN : il mérite un verdict visible.
 */
export const diagnoseSiret = (
  siret: string | null,
): { label: string; tone: 'ok' | 'warn' | 'error' } => {
  const digits = (siret ?? '').replace(/[^0-9]/g, '');
  if (digits.length === 14) return { label: 'SIRET complet', tone: 'ok' };
  if (digits.length === 9) return { label: 'SIREN seul', tone: 'warn' };
  return { label: 'inexploitable', tone: 'error' };
};

/**
 * Adresse affichable, sans doublon de code postal ni de ville.
 *
 * `organizations.address` vient de Pipedrive en texte libre et contient souvent
 * DÉJÀ le code postal et la ville (org 470 : « 18 rue du garay  43120 Monistrol
 * sur Loire , »). Concaténer aveuglément `address + postCode + town` affiche
 * alors la ville deux fois. On ne recolle donc la queue que si elle manque.
 */
export const formatAgencyAddress = (
  address: string | null,
  postCode: string | null,
  town: string | null,
): string => {
  const base = (address ?? '').trim().replace(/[\s,]+$/, '');
  const tail = [postCode, town].filter(Boolean).join(' ').trim();
  if (!base) return tail;
  if (!tail) return base;
  const squash = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');
  return squash(base).includes(squash(tail)) ? base : `${base} ${tail}`;
};

const TONE_COLOR: Record<'ok' | 'warn' | 'error', string> = {
  ok: tokens.color.green,
  warn: tokens.color.orange,
  error: tokens.color.red,
};

const SectionTitle = ({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) => (
  <Box sx={{ mb: 3 }}>
    <Stack direction="row" spacing={2} alignItems="center">
      <Box sx={{ color: tokens.color.textFaint, display: 'flex' }}>{icon}</Box>
      <Typography variant="h4">{title}</Typography>
    </Stack>
    {hint && (
      <Typography variant="body2" sx={{ color: tokens.color.textFaint, mt: 1 }}>
        {hint}
      </Typography>
    )}
  </Box>
);

const Field = ({ label, value, mono }: { label: string; value: React.ReactNode; mono?: boolean }) => (
  <Box sx={{ minWidth: 160 }}>
    <Typography variant="overline" sx={{ display: 'block', color: tokens.color.textFaint }}>
      {label}
    </Typography>
    <Typography
      variant="body1"
      sx={{ fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined, wordBreak: 'break-word' }}
    >
      {value === null || value === undefined || value === '' ? (
        <Box component="span" sx={{ color: tokens.color.textFaint }}>
          non renseigné
        </Box>
      ) : (
        value
      )}
    </Typography>
  </Box>
);

/** Encart d'avertissement, décliné du design system plutôt que de l'Alert MUI par défaut. */
const NoticeCard = ({
  tone,
  icon,
  title,
  children,
}: {
  tone: 'warn' | 'error' | 'info';
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
}) => {
  const accent = { warn: tokens.color.orange, error: tokens.color.red, info: tokens.color.blue }[tone];
  const background = { warn: tokens.color.orangeLight, error: tokens.color.redLight, info: tokens.color.blueLight }[
    tone
  ];
  return (
    <Box sx={{ ...sxCardAccent(accent), backgroundColor: background, p: 5, mb: 5 }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Box sx={{ color: accent, display: 'flex' }}>{icon}</Box>
        <Typography variant="h6">{title}</Typography>
      </Stack>
      {children}
    </Box>
  );
};

const AccountsTable = ({ accounts }: { accounts: AgencyAccount[] }) => (
  <Table size="small">
    <TableHead sx={sxTableHead}>
      <TableRow>
        <TableCell>Email de connexion</TableCell>
        <TableCell>Franchise</TableCell>
        <TableCell>UID Firebase</TableCell>
        <TableCell>Org (table)</TableCell>
        <TableCell>Org (Firestore, fait foi)</TableCell>
        <TableCell>Rôle</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {accounts.map((account) => (
        <TableRow key={account.id} hover>
          <TableCell>{account.orgEmail}</TableCell>
          <TableCell>{account.orgNetwork}</TableCell>
          <TableCell sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 11 }}>
            {account.firebaseId}
          </TableCell>
          <TableCell>{account.orgId}</TableCell>
          <TableCell>
            {account.firestore.readError ? (
              <Tooltip title={account.firestore.readError}>
                <Chip label="Firestore injoignable" size="small" color="warning" />
              </Tooltip>
            ) : !account.firestore.exists ? (
              <Chip label="document absent" size="small" color="error" />
            ) : (
              <Stack direction="row" spacing={1} alignItems="center">
                <span>{account.firestore.idPipedrive}</span>
                {account.isLinkDiverging && <Chip label="divergence" size="small" color="error" />}
              </Stack>
            )}
          </TableCell>
          <TableCell>{account.firestore.role}</TableCell>
        </TableRow>
      ))}
    </TableBody>
  </Table>
);

const ProfileInner = ({ profile, onRefresh }: { profile: AgencyProfileData; onRefresh: () => void }) => {
  const { organization, counts, persons, accounts, accountsHoldingEmail, siblings, entityChanges, qonto } = profile;
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const siret = diagnoseSiret(organization.siret);
  const divergingAccounts = accounts.filter((account) => account.isLinkDiverging);
  // Comptes portant l'email mais rattachés AILLEURS : c'est exactement ce qui
  // fait échouer une ouverture de compte avec une adresse déjà connue.
  const foreignEmailHolders = accountsHoldingEmail.filter((account) => account.orgId !== organization.id);
  const dossiers = counts.cgRegistrations + counts.daRegistrations + counts.carDeals;

  return (
    <Box sx={{ backgroundColor: tokens.color.bg, minHeight: '100vh', pb: 10 }}>
      <Box sx={{ px: 8, pt: 5, pb: 3 }}>
        <Button component={RouterLink} to="/admin/org-persons" startIcon={<ArrowBackIcon />} size="small">
          Organisations
        </Button>
      </Box>

      {/* Bandeau d'identité : ce qu'on doit lire avant de toucher à quoi que ce soit. */}
      <Box sx={{ px: 8 }}>
        <Box sx={{ ...sxCard, backgroundColor: tokens.color.dark, p: 6, color: tokens.color.white }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', md: 'center' }}
            spacing={3}
          >
            <Box>
              <Typography variant="overline" sx={{ color: tokens.color.yellow }}>
                Fiche agence
              </Typography>
              <Typography variant="h2" sx={{ color: tokens.color.white, mt: 1 }}>
                {organization.name}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 3 }} flexWrap="wrap" useFlexGap>
                <Chip label={`org ${organization.id}`} size="small" />
                {organization.franchiseId && <Chip label={`franchise ${organization.franchiseId}`} size="small" />}
                {organization.deletedAt && <Chip label="supprimée dans Pipedrive" size="small" color="error" />}
              </Stack>
            </Box>
            <Button variant="contained" startIcon={<SwapHorizIcon />} onClick={() => setIsDialogOpen(true)}>
              Changer d'entité
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={8}
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: 6, pt: 5, borderTop: `1px solid ${tokens.color.darkBorder}` }}
          >
            <Box sx={{ minWidth: 160 }}>
              <Typography variant="overline" sx={{ display: 'block', color: tokens.color.textFaint }}>
                SIRET
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center">
                <Typography variant="body1" sx={{ color: tokens.color.white }}>
                  {organization.siret ?? '—'}
                </Typography>
                <Chip
                  label={siret.label}
                  size="small"
                  sx={{ backgroundColor: TONE_COLOR[siret.tone], color: tokens.color.white, fontWeight: 700 }}
                />
              </Stack>
            </Box>
            {[
              { label: 'Email', value: organization.email },
              {
                label: 'Adresse',
                value: formatAgencyAddress(organization.address, organization.postCode, organization.town),
              },
              { label: 'Signataire', value: organization.personName },
              { label: 'Synchro Pipedrive', value: organization.updatedAt },
            ].map((field) => (
              <Box key={field.label} sx={{ minWidth: 160 }}>
                <Typography variant="overline" sx={{ display: 'block', color: tokens.color.textFaint }}>
                  {field.label}
                </Typography>
                <Typography variant="body1" sx={{ color: tokens.color.white }}>
                  {field.value || '—'}
                </Typography>
              </Box>
            ))}
          </Stack>
        </Box>
      </Box>

      <Box sx={{ px: 8, mt: 5 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mb: 5 }}>
          <StatCard label="Cartes grises" value={counts.cgRegistrations} accent="yellow" />
          <StatCard label="Attestations" value={counts.daRegistrations} accent="yellow" />
          <StatCard label="Deals" value={counts.carDeals} accent="yellow" />
          <StatCard label="Comptes" value={accounts.length} accent={accounts.length === 0 ? 'red' : 'green'} />
          <StatCard label="Feature flags" value={counts.featureFlags} accent="yellow" />
          <StatCard label="Personnes" value={persons.length} accent="yellow" />
        </Stack>

        {siblings.length > 0 && (
          <NoticeCard
            tone="warn"
            icon={<WarningAmberIcon />}
            title={
              siblings.length === 1
                ? 'Une organisation sœur partage cet exploitant'
                : `${siblings.length} organisations sœurs partagent cet exploitant`
            }
          >
            <Typography variant="body2" sx={{ mb: 3 }}>
              Rapprochées par email, que l'exploitant conserve d'une entité à l'autre, ou par SIREN, identique lors d'un
              simple transfert d'établissement. Celle qui porte les dossiers est l'organisation vivante ; l'autre est le
              plus souvent le doublon créé par le Typeform.
            </Typography>
            <Box sx={{ ...sxCard, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead sx={sxTableHead}>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>SIRET</TableCell>
                    <TableCell>Rapprochée par</TableCell>
                    <TableCell align="right">CG</TableCell>
                    <TableCell align="right">DA</TableCell>
                    <TableCell align="right">Deals</TableCell>
                    <TableCell align="right">Comptes</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow sx={{ backgroundColor: tokens.color.yellowLight }}>
                    <TableCell>
                      <strong>{organization.name}</strong>
                      <Chip label="celle-ci" size="small" sx={{ ml: 2 }} />
                    </TableCell>
                    <TableCell>{organization.siret}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell align="right">{counts.cgRegistrations}</TableCell>
                    <TableCell align="right">{counts.daRegistrations}</TableCell>
                    <TableCell align="right">{counts.carDeals}</TableCell>
                    <TableCell align="right">{accounts.length}</TableCell>
                  </TableRow>
                  {siblings.map((sibling) => (
                    <TableRow key={sibling.id} hover>
                      <TableCell>
                        <Link component={RouterLink} to={`/admin/agency/${sibling.id}`}>
                          {sibling.name}
                        </Link>
                        <Chip label={`org ${sibling.id}`} size="small" sx={{ ml: 2 }} />
                      </TableCell>
                      <TableCell>{sibling.siret}</TableCell>
                      <TableCell>{sibling.matchReason === 'email' ? 'email' : 'SIREN'}</TableCell>
                      <TableCell align="right">{sibling.counts.cgRegistrations}</TableCell>
                      <TableCell align="right">{sibling.counts.daRegistrations}</TableCell>
                      <TableCell align="right">{sibling.counts.carDeals}</TableCell>
                      <TableCell align="right">{sibling.accountCount}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          </NoticeCard>
        )}

        {divergingAccounts.length > 0 && (
          <NoticeCard tone="error" icon={<ErrorOutlineIcon />} title="Rattachement incohérent entre Firestore et la base">
            <Typography variant="body2">
              Firestore fait foi à la connexion : ces comptes ouvrent en réalité une autre agence que celle affichée par
              la table <code>firebase_accounts</code>. Corriger la table seule ne changerait rien.
            </Typography>
          </NoticeCard>
        )}

        <Box sx={{ ...sxCard, p: 6, mb: 5 }}>
          <SectionTitle
            icon={<VpnKeyIcon />}
            title="Comptes de connexion"
            hint="L'identité vit dans Firebase Auth, et le lien compte ↔ agence dans Firestore (Users/{uid}.idPipedrive). La table firebase_accounts n'est qu'une copie de confort, jamais relue à l'authentification."
          />
          {accounts.length === 0 ? (
            <Typography variant="body2" sx={{ color: tokens.color.textFaint }}>
              Aucun compte Skipcar rattaché à cette organisation.
            </Typography>
          ) : (
            <AccountsTable accounts={accounts} />
          )}

          {foreignEmailHolders.length > 0 && (
            <Box sx={{ mt: 5 }}>
              <NoticeCard
                tone="info"
                icon={<BusinessIcon />}
                title="Cet email est déjà porté par un compte d'une autre organisation"
              >
                {foreignEmailHolders.map((account) => (
                  <Typography variant="body2" key={account.id}>
                    {account.orgEmail} → {account.orgName} (org {account.orgId})
                  </Typography>
                ))}
                <Typography variant="body2" sx={{ mt: 2 }}>
                  C'est la cause du refus « adresse déjà utilisée » à la création d'un compte : Firebase Auth impose
                  l'unicité de l'email.
                </Typography>
              </NoticeCard>
            </Box>
          )}
        </Box>

        <Box sx={{ ...sxCard, p: 6 }}>
          <SectionTitle
            icon={<ReceiptLongIcon />}
            title="Facturation Qonto"
            hint="Le client Qonto porte la cadence de facturation et le rattachement franchise, pas l'agence. En créer un nouveau lors d'un changement d'entité ferait perdre les deux, dont le RFA."
          />
          {qonto.clientId ? (
            <>
              <Stack direction="row" spacing={8} flexWrap="wrap" useFlexGap>
                <Field label="Client Qonto" value={qonto.clientId} mono />
                <Field label="Cadence" value={qonto.invoicingCadence ?? 'non définie'} />
                <Field
                  label="Rattaché à la franchise"
                  value={
                    <Chip
                      label={qonto.mappedToFranchise ? 'oui' : 'non'}
                      size="small"
                      color={qonto.mappedToFranchise ? 'success' : 'warning'}
                    />
                  }
                />
              </Stack>
              {!qonto.mappedToFranchise && (
                <Typography variant="body2" sx={{ mt: 3, color: tokens.color.orange }}>
                  Client absent de <code>franchise_client_mapping</code> : le RFA ne le verra pas.
                </Typography>
              )}
            </>
          ) : (
            <Typography variant="body2" sx={{ color: tokens.color.textFaint }}>
              Aucun client Qonto résolu pour cette organisation (aucune facture émise à ce jour).
            </Typography>
          )}
        </Box>

        {entityChanges.length > 0 && (
          <Box sx={{ ...sxCard, p: 6, mt: 5 }}>
            <SectionTitle
              icon={<HistoryIcon />}
              title="Historique des changements d'entité"
              hint="La date d'effet est celle du Kbis, pas celle de la saisie : c'est elle qui dira sous quelle entité un dossier ancien a été traité."
            />
            <Table size="small">
              <TableHead sx={sxTableHead}>
                <TableRow>
                  <TableCell>Date d'effet</TableCell>
                  <TableCell>Avant</TableCell>
                  <TableCell>Après</TableCell>
                  <TableCell>Propagation</TableCell>
                  <TableCell>Par</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {entityChanges.map((change) => (
                  <TableRow key={change.id} hover>
                    <TableCell>{change.effective_date?.slice(0, 10)}</TableCell>
                    <TableCell>
                      {change.previous_name}
                      <Box component="span" sx={{ color: tokens.color.textFaint }}> · {change.previous_siret}</Box>
                    </TableCell>
                    <TableCell>
                      <strong>{change.new_name}</strong>
                      <Box component="span" sx={{ color: tokens.color.textFaint }}> · {change.new_siret}</Box>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={1}>
                        <Chip
                          label="Pipedrive"
                          size="small"
                          color={change.pipedrive_updated ? 'success' : 'error'}
                          variant={change.pipedrive_updated ? 'filled' : 'outlined'}
                        />
                        <Chip
                          label="Qonto"
                          size="small"
                          color={change.qonto_updated ? 'success' : 'error'}
                          variant={change.qonto_updated ? 'filled' : 'outlined'}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>{change.performed_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <EntityChangeDialog
          open={isDialogOpen}
          organization={organization}
          onClose={() => setIsDialogOpen(false)}
          onApplied={onRefresh}
        />

        <Typography variant="caption" sx={{ display: 'block', mt: 5, color: tokens.color.textFaint }}>
          {dossiers === 0
            ? "Aucun dossier : cette organisation est inerte, c'est probablement un doublon."
            : `${dossiers} dossiers rattachés. Un changement d'entité doit les conserver.`}
        </Typography>
      </Box>
    </Box>
  );
};

const AgencyProfile = () => {
  const { orgId } = useParams<{ orgId: string }>();
  const { profile, isLoading, error, refetch } = useAgencyProfile(orgId);

  return (
    <NextThemeProvider>
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}>
          <CircularProgress />
        </Box>
      ) : error || !profile ? (
        <Box sx={{ p: 8 }}>
          <NoticeCard tone="error" icon={<ErrorOutlineIcon />} title="Fiche agence indisponible">
            <Typography variant="body2">{error ?? 'Organisation introuvable.'}</Typography>
          </NoticeCard>
        </Box>
      ) : (
        <ProfileInner profile={profile} onRefresh={refetch} />
      )}
    </NextThemeProvider>
  );
};

export default AgencyProfile;
